import { useEffect } from 'react';
import { create } from 'zustand';
import {
  DEFAULT_WORKSPACE_SETTINGS,
  detectCountry,
  detectTimezone,
  isValidTimezone,
  resolveTimezone,
  type PublicUser,
  type WorkspaceSettings,
} from '@itsappy/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';

/**
 * The zone every timestamp on screen is rendered in.
 *
 * Held in a store rather than threaded through the ~40 places that format a
 * date: there is exactly one signed-in person, so there is exactly one answer,
 * and passing it down by hand would leave a long tail of components quietly
 * still rendering in the device's zone — which is the bug being fixed.
 */
interface TimezoneState {
  /** The resolved zone: the person's own choice, or their device. */
  zone: string;
  /** The workspace's declared zone, empty when it has not set one. */
  workspaceZone: string;
  setZones: (zone: string, workspaceZone: string) => void;
}

const useTimezoneStore = create<TimezoneState>((set) => ({
  zone: detectTimezone(),
  workspaceZone: '',
  setZones: (zone, workspaceZone) => set({ zone, workspaceZone }),
}));

/**
 * The current zone, read outside React.
 *
 * The formatters in `lib/utils.ts` call this. They are plain functions by
 * design — they are called from render bodies, `useMemo`s and sort
 * comparators alike, and a hook would not be legal in most of them.
 */
export function activeTimezone(): string {
  return useTimezoneStore.getState().zone;
}

/** The current zone, as a subscription — re-renders when it changes. */
export function useTimezone(): string {
  return useTimezoneStore((state) => state.zone);
}

/** The workspace's declared zone, or `''` when it has none. */
export function useWorkspaceTimezone(): string {
  return useTimezoneStore((state) => state.workspaceZone);
}

function settingsOf(organizations: { id: string; settings?: WorkspaceSettings }[], orgId: string | null) {
  const found = organizations.find((org) => org.id === orgId);
  return { ...DEFAULT_WORKSPACE_SETTINGS, ...(found?.settings ?? {}) };
}

/**
 * Keeps the rendering zone in step with the signed-in account, and — while
 * that account is on auto — keeps the server's copy in step with the device.
 *
 * The upload matters beyond display: quiet hours and every push the server
 * sends on a schedule are evaluated server-side, where the only zone available
 * is the one on the record. Someone who moves country and never opens Settings
 * should still stop getting a 3am notification.
 */
export function useTimezoneSync(): void {
  const user = useAuth((state) => state.user);
  const orgId = useAuth((state) => state.orgId);
  const organizations = useAuth((state) => state.organizations);
  const setUser = useAuth((state) => state.setUser);
  const setZones = useTimezoneStore((state) => state.setZones);

  const workspaceZone = settingsOf(organizations, orgId).timezone;
  const auto = user?.autoTimezone !== false;
  const savedZone = user?.timezone;

  useEffect(() => {
    const zone = resolveTimezone({
      userTimezone: savedZone,
      auto,
      workspaceTimezone: workspaceZone,
    });
    setZones(zone, isValidTimezone(workspaceZone) ? workspaceZone : '');
  }, [savedZone, auto, workspaceZone, setZones]);

  useEffect(() => {
    if (!user || !auto) return;
    const detected = detectTimezone();
    if (detected === savedZone) return;
    // Best-effort. A failure here costs an out-of-date zone on the server, not
    // a broken screen, so it must never surface as an error to the person.
    void api
      .patch<PublicUser>('/users/me', { timezone: detected, autoTimezone: true })
      .then(setUser)
      .catch(() => undefined);
  }, [user, auto, savedZone, setUser]);
}

/**
 * What the device reports, as a picker value — the starting point for the
 * "use my device" affordance in every form that asks for a zone.
 */
export function detectedTimezoneValue(): { country: string; timezone: string } {
  return { country: detectCountry() ?? '', timezone: detectTimezone() };
}
